import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Lenis from '@studio-freight/lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import Header  from './Components/Header/Header';
import Home    from './Components/Home/Home';
import About   from './Components/About/About';
import Work    from './Components/Work/Work';
import Contact from './Components/Contact/Contact';
import Footer  from './Components/Footer/Footer';

import data from './Utils/data';
import './App.css';

/* Most visitors never open the blog, so it stays off the first load. */
const Blog     = lazy(() => import('./Components/Blog/Blog'));
const BlogPost = lazy(() => import('./Components/Blog/BlogPost'));

gsap.registerPlugin(ScrollTrigger);

const pageAnim = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.32, ease: 'easeOut' } },
  exit:    { opacity: 0, transition: { duration: 0.2, ease: 'easeIn' } },
};

function Page({ children }) {
  return (
    <motion.div variants={pageAnim} initial="initial" animate="animate" exit="exit">
      {children}
    </motion.div>
  );
}

function RouteFallback() {
  return <div className="route-fallback" aria-hidden="true" />;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const id = requestAnimationFrame(() => ScrollTrigger.refresh());
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Page><Home /></Page>} />

        {/* Each act also stands alone so existing links keep resolving.
            `standalone` lets the section own the document head only when
            it is the whole page. */}
        <Route path="/about"   element={<Page><About   data={data.about} standalone /></Page>} />
        <Route path="/work"    element={<Page><Work    data={data.selectedWork} standalone /></Page>} />
        <Route path="/contact" element={<Page><Contact data={data.contact} personal={data.personal} standalone /></Page>} />

        <Route path="/blog"       element={<Page><Suspense fallback={<RouteFallback />}><Blog /></Suspense></Page>} />
        <Route path="/blog/:slug" element={<Page><Suspense fallback={<RouteFallback />}><BlogPost /></Suspense></Page>} />
      </Routes>
    </AnimatePresence>
  );
}

function Layout({ children }) {
  /* Smooth scroll, kept deliberately light.
     The earlier duration-based config plus a long pinned section made
     scrolling feel like wading. `lerp` tracks the wheel far more closely
     than an eased duration does, and the multiplier puts travel back at
     roughly native distance per tick. Driven off the GSAP ticker so there
     is one animation loop rather than two competing ones. */
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const lenis = new Lenis({
      lerp: 0.14,
      wheelMultiplier: 1.15,
      smoothWheel: true,
      syncTouch: false,
    });

    lenis.on('scroll', ScrollTrigger.update);

    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(tick);
      gsap.ticker.lagSmoothing(500, 33);
      lenis.destroy();
    };
  }, []);

  /* The olive page with the bone panel floated inside it. This wrapper
     is the design's central structural idea, so it lives at the root
     rather than being repeated per section. */
  return (
    <div className="frame">
      <div className="panel">
        <Header />
        <main className="main">{children}</main>
        <Footer personal={data.personal} />
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Layout>
        <AnimatedRoutes />
      </Layout>
    </Router>
  );
}

export default App;
